// =====================================================
// DIFF ENGINE — Myers-like word-level diff
// Includes move detection for all tiers.
// =====================================================

import type { RichParagraph, DiffEntry, DiffParagraph, DiffResult } from '../types';
import { detectMoves } from './move-detection';
import { countWords } from './counts';
import { TUNING } from './tuning';
import { wordOf, withWordIndices } from './word-index';
export { wordOf, withWordIndices } from './word-index';

export function tokenize(text: string | null | undefined): string[] {
  if (text == null) return [];
  const tokens: string[] = [];
  const regex = /\S+/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    tokens.push(match[0]);
  }
  return tokens;
}

const _nqCache = new Map<string, string>();
const _NQ_CACHE_MAX = 2000;

export function normalizeQuotes(text: string | null | undefined): string {
  if (text == null) return '';
  const cached = _nqCache.get(text);
  if (cached !== undefined) return cached;
  const result = text
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    .replace(/[\u00AB\u00BB]/g, '"')
    .replace(/\u2014/g, '--')
    .replace(/\u2013/g, '-')
    .replace(/\u00A0/g, ' ');
  if (_nqCache.size >= _NQ_CACHE_MAX) _nqCache.clear();
  _nqCache.set(text, result);
  return result;
}

export function normalizeText(text: string | null | undefined): string {
  if (text == null) return '';
  let t = normalizeQuotes(text);
  t = t.normalize('NFC');
  t = t.replace(/[\u200B\u200C\u200D\u200E\u200F\uFEFF\u2060\u00AD\u034F\u061C\u2028\u2029]/g, '');
  t = t.replace(/\u00A0/g, ' ');
  t = t.replace(/[ \t]+/g, ' ');
  return t.trim();
}

export function wordsEqual(a: string, b: string): boolean {
  if (a === b) return true;
  const na = normalizeText(a), nb = normalizeText(b);
  if (na === nb) return true;
  // No further normalization — punctuation changes (added comma, removed period,
  // changed parentheses) and CASE changes are meaningful in legal documents and
  // must be detected: capitalising "energization date" into the defined term
  // "Energization Date" is a substantive edit, and Word's Compare shows it too.
  // (Case-insensitive matching used to hide these; it also made "reject all" on the
  // output unable to restore the original's spelling.) Quote/dash normalization
  // above still absorbs Word's curly-quote auto-formatting noise.
  return false;
}

export function diffArrays(a: string[], b: string[], eqFn?: (x: string, y: string) => boolean): DiffEntry[] {
  const eq = eqFn || ((x: string, y: string) => x === y);
  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return b.map((v, i) => ({ type: 'insert' as const, value: v, idx: i }));
  if (b.length === 0) return a.map((v, i) => ({ type: 'delete' as const, value: v, idx: i }));

  // Strip common prefix
  let prefixLen = 0;
  while (prefixLen < a.length && prefixLen < b.length && eq(a[prefixLen], b[prefixLen])) {
    prefixLen++;
  }
  // Strip common suffix
  let suffixLen = 0;
  while (suffixLen < (a.length - prefixLen) && suffixLen < (b.length - prefixLen) &&
         eq(a[a.length - 1 - suffixLen], b[b.length - 1 - suffixLen])) {
    suffixLen++;
  }

  const result: DiffEntry[] = [];
  for (let i = 0; i < prefixLen; i++) {
    result.push({ type: 'equal', value: b[i], aIdx: i, bIdx: i });
  }

  const trimmedA = a.slice(prefixLen, a.length - suffixLen);
  const trimmedB = b.slice(prefixLen, b.length - suffixLen);

  if (trimmedA.length === 0 && trimmedB.length === 0) {
    // nothing in the middle
  } else if (trimmedA.length === 0) {
    for (let i = 0; i < trimmedB.length; i++) {
      result.push({ type: 'insert', value: trimmedB[i], idx: prefixLen + i });
    }
  } else if (trimmedB.length === 0) {
    for (let i = 0; i < trimmedA.length; i++) {
      result.push({ type: 'delete', value: trimmedA[i], idx: prefixLen + i });
    }
  } else {
    // equal words share a key, so a long middle can be compared as integers (see _largeDiff)
    const keyOf = !eqFn ? ((x: string) => x) : eqFn === wordsEqual ? normalizeText : null;
    const midDiff = _myersDiff(trimmedA, trimmedB, eq, prefixLen, keyOf);
    for (const d of midDiff) result.push(d);
  }

  // Emit suffix as equal
  for (let i = 0; i < suffixLen; i++) {
    const ai = a.length - suffixLen + i;
    const bi = b.length - suffixLen + i;
    result.push({ type: 'equal', value: b[bi], aIdx: ai, bIdx: bi });
  }

  return result;
}

function _myersDiff(a: string[], b: string[], eq: (x: string, y: string) => boolean, offset: number, keyOf: ((x: string) => string) | null = null): DiffEntry[] {
  const N = a.length, M = b.length;
  const MAX = N + M;

  if (MAX > 5000) {
    return _largeDiff(a, b, eq, offset, keyOf);
  }

  const vSize = 2 * MAX + 1;
  const vOff = MAX;
  const v = new Int32Array(vSize);
  v.fill(-1);
  v[vOff + 1] = 0;

  const trace: Int32Array[] = [];

  for (let d = 0; d <= MAX; d++) {
    for (let k = -d; k <= d; k += 2) {
      let x: number;
      if (k === -d || (k !== d && v[vOff + k - 1] < v[vOff + k + 1])) {
        x = v[vOff + k + 1];
      } else {
        x = v[vOff + k - 1] + 1;
      }
      let y = x - k;
      while (x < N && y < M && eq(a[x], b[y])) {
        x++; y++;
      }
      v[vOff + k] = x;
      if (x >= N && y >= M) {
        trace.push(v.slice());
        return _doBacktrack(trace, a, b, N, M, vOff, offset);
      }
    }
    trace.push(v.slice());
  }
  return _lcsBasedDiff(a, b, eq, offset);
}

function _doBacktrack(trace: Int32Array[], a: string[], b: string[], N: number, M: number, vOff: number, offset: number): DiffEntry[] {
  const edits: DiffEntry[] = [];
  let x = N, y = M;
  const D = trace.length - 1;

  for (let d = D; d > 0; d--) {
    const vPrev = trace[d - 1];
    const k = x - y;

    let prevK: number;
    if (k === -d || (k !== d && vPrev[vOff + k - 1] < vPrev[vOff + k + 1])) {
      prevK = k + 1;
    } else {
      prevK = k - 1;
    }

    const prevEndX = vPrev[vOff + prevK];
    const prevEndY = prevEndX - prevK;

    let snakeStartX: number, snakeStartY: number;
    if (k > prevK) {
      snakeStartX = prevEndX + 1;
      snakeStartY = prevEndY;
    } else {
      snakeStartX = prevEndX;
      snakeStartY = prevEndY + 1;
    }

    while (x > snakeStartX && y > snakeStartY) {
      x--; y--;
      edits.push({ type: 'equal', value: b[y], aIdx: x + offset, bIdx: y + offset });
    }

    if (k > prevK) {
      x--;
      edits.push({ type: 'delete', value: a[x], idx: x + offset });
    } else {
      y--;
      edits.push({ type: 'insert', value: b[y], idx: y + offset });
    }
  }

  while (x > 0 && y > 0) {
    x--; y--;
    edits.push({ type: 'equal', value: b[y], aIdx: x + offset, bIdx: y + offset });
  }
  while (x > 0) {
    x--;
    edits.push({ type: 'delete', value: a[x], idx: x + offset });
  }
  while (y > 0) {
    y--;
    edits.push({ type: 'insert', value: b[y], idx: y + offset });
  }

  edits.reverse();
  return edits;
}

/**
 * A middle too big for the full-trace Myers above (N + M > 5000 words: a very long paragraph,
 * which used to take minutes and come out mostly as delete + insert). Words become integer ids
 * first when equality has a key (equal words, equal ids), so comparing is cheap. Myers' search
 * then keeps only the band of each step (D² memory instead of D·(N+M)), which is instant for a
 * long paragraph with a few edits; past D_LIMIT edits, Hirschberg's O(N·M) LCS.
 */
const D_LIMIT = 2000;
function _largeDiff(a: string[], b: string[], eq: (x: string, y: string) => boolean, offset: number, keyOf: ((x: string) => string) | null): DiffEntry[] {
  let A: any = a, B: any = b, E: (x: any, y: any) => boolean = eq;
  if (keyOf) {
    const ids = new Map<string, number>();
    const id = (w: string) => { const k = keyOf(w); let v = ids.get(k); if (v === undefined) { v = ids.size; ids.set(k, v); } return v; };
    A = Int32Array.from(a, (w) => id(w)); B = Int32Array.from(b, (w) => id(w)); E = (x, y) => x === y;
  }
  let out = _myersBanded(A, B, E, offset, D_LIMIT);
  if (!out) { out = []; _hirschberg(A, 0, A.length, B, 0, B.length, E, offset, out); }
  if (keyOf) for (const d of out) d.value = d.type === 'delete' ? a[d.idx! - offset] : b[(d.type === 'equal' ? d.bIdx! : d.idx!) - offset];
  return out;
}

/** Myers' O((N+M)·D) diff keeping, for each step d, only the diagonals -d..d (trace[d][k + d]).
 *  Null when the edit distance exceeds dLimit. */
function _myersBanded(a: ArrayLike<any>, b: ArrayLike<any>, eq: (x: any, y: any) => boolean, offset: number, dLimit: number): DiffEntry[] | null {
  const N = a.length, M = b.length, MAX = N + M, vOff = MAX + 1;
  const v = new Int32Array(2 * MAX + 3);
  v.fill(-1);
  v[vOff + 1] = 0;
  const trace: Int32Array[] = [];
  for (let d = 0; d <= Math.min(MAX, dLimit); d++) {
    for (let k = -d; k <= d; k += 2) {
      let x = (k === -d || (k !== d && v[vOff + k - 1] < v[vOff + k + 1])) ? v[vOff + k + 1] : v[vOff + k - 1] + 1;
      let y = x - k;
      while (x < N && y < M && eq(a[x], b[y])) { x++; y++; }
      v[vOff + k] = x;
      if (x >= N && y >= M) {
        trace.push(v.slice(vOff - d, vOff + d + 1));
        return _backtrackBanded(trace, a, b, N, M, offset);
      }
    }
    trace.push(v.slice(vOff - d, vOff + d + 1));
  }
  return null;
}

function _backtrackBanded(trace: Int32Array[], a: ArrayLike<any>, b: ArrayLike<any>, N: number, M: number, offset: number): DiffEntry[] {
  const edits: DiffEntry[] = [];
  let x = N, y = M;
  for (let d = trace.length - 1; d > 0; d--) {
    const vp = trace[d - 1], base = d - 1;   // vp[k + base]: furthest x on diagonal k after step d-1
    const k = x - y;
    const prevK = (k === -d || (k !== d && vp[k - 1 + base] < vp[k + 1 + base])) ? k + 1 : k - 1;
    const prevEndX = vp[prevK + base], prevEndY = prevEndX - prevK;
    const snakeStartX = k > prevK ? prevEndX + 1 : prevEndX;
    const snakeStartY = k > prevK ? prevEndY : prevEndY + 1;
    while (x > snakeStartX && y > snakeStartY) { x--; y--; edits.push({ type: 'equal', value: b[y], aIdx: x + offset, bIdx: y + offset }); }
    if (k > prevK) { x--; edits.push({ type: 'delete', value: a[x], idx: x + offset }); }
    else { y--; edits.push({ type: 'insert', value: b[y], idx: y + offset }); }
  }
  while (x > 0 && y > 0) { x--; y--; edits.push({ type: 'equal', value: b[y], aIdx: x + offset, bIdx: y + offset }); }
  while (x > 0) { x--; edits.push({ type: 'delete', value: a[x], idx: x + offset }); }
  while (y > 0) { y--; edits.push({ type: 'insert', value: b[y], idx: y + offset }); }
  edits.reverse();
  return edits;
}

/** Hirschberg's algorithm: O(m*n) time, O(n) space LCS-based diff.
 *  Avoids the m*n dirs matrix that can blow up memory on large paragraphs. */
function _lcsBasedDiff(a: string[], b: string[], eq: (x: string, y: string) => boolean, offset: number): DiffEntry[] {
  const result: DiffEntry[] = [];
  _hirschberg(a, 0, a.length, b, 0, b.length, eq, offset, result);
  return result;
}

/** Compute last row of LCS lengths for a[aStart..aEnd] vs b[bStart..bEnd] */
function _lcsLastRow(a: string[], aStart: number, aEnd: number, b: string[], bStart: number, bEnd: number, eq: (x: string, y: string) => boolean): Uint32Array {
  const n = bEnd - bStart;
  const prev = new Uint32Array(n + 1);
  const curr = new Uint32Array(n + 1);
  for (let i = aStart; i < aEnd; i++) {
    curr.fill(0);
    for (let j = 0; j < n; j++) {
      if (eq(a[i], b[bStart + j])) {
        curr[j + 1] = prev[j] + 1;
      } else {
        curr[j + 1] = Math.max(prev[j + 1], curr[j]);
      }
    }
    prev.set(curr);
  }
  return prev;
}

function _hirschberg(
  a: string[], aStart: number, aEnd: number,
  b: string[], bStart: number, bEnd: number,
  eq: (x: string, y: string) => boolean,
  offset: number,
  result: DiffEntry[]
): void {
  const m = aEnd - aStart;
  const n = bEnd - bStart;

  if (m === 0) {
    for (let j = bStart; j < bEnd; j++) {
      result.push({ type: 'insert', value: b[j], idx: j + offset });
    }
    return;
  }
  if (n === 0) {
    for (let i = aStart; i < aEnd; i++) {
      result.push({ type: 'delete', value: a[i], idx: i + offset });
    }
    return;
  }
  if (m === 1) {
    // Base case: single element in a
    let found = -1;
    for (let j = bStart; j < bEnd; j++) {
      if (eq(a[aStart], b[j])) { found = j; break; }
    }
    if (found >= 0) {
      for (let j = bStart; j < found; j++) {
        result.push({ type: 'insert', value: b[j], idx: j + offset });
      }
      result.push({ type: 'equal', value: b[found], aIdx: aStart + offset, bIdx: found + offset });
      for (let j = found + 1; j < bEnd; j++) {
        result.push({ type: 'insert', value: b[j], idx: j + offset });
      }
    } else {
      result.push({ type: 'delete', value: a[aStart], idx: aStart + offset });
      for (let j = bStart; j < bEnd; j++) {
        result.push({ type: 'insert', value: b[j], idx: j + offset });
      }
    }
    return;
  }

  // Divide: split a in half
  const aMid = aStart + Math.floor(m / 2);

  // Forward pass: LCS lengths for a[aStart..aMid] vs b[bStart..bEnd]
  const topRow = _lcsLastRow(a, aStart, aMid, b, bStart, bEnd, eq);

  // Reverse pass: LCS lengths for a[aMid..aEnd] reversed vs b[bStart..bEnd] reversed
  const botRow = _lcsLastRowReverse(a, aMid, aEnd, b, bStart, bEnd, eq);

  // Find optimal split point in b. topRow[j] is the LCS of the top half with b[bStart..bStart+j),
  // botRow[j] that of the bottom half with b[bStart+j..bEnd) (the reverse pass indexes by where
  // the rest of b starts, not by its length: pairing topRow[j] with botRow[n - j] split at the
  // wrong place, and long paragraphs came out mostly as delete + insert)
  let bestJ = bStart;
  let bestSum = topRow[0] + botRow[0];
  for (let j = 0; j <= n; j++) {
    const sum = topRow[j] + botRow[j];
    if (sum > bestSum) {
      bestSum = sum;
      bestJ = bStart + j;
    }
  }

  // Conquer: recurse on both halves
  _hirschberg(a, aStart, aMid, b, bStart, bestJ, eq, offset, result);
  _hirschberg(a, aMid, aEnd, b, bestJ, bEnd, eq, offset, result);
}

/** Compute last row of LCS lengths for reversed sequences */
function _lcsLastRowReverse(a: string[], aStart: number, aEnd: number, b: string[], bStart: number, bEnd: number, eq: (x: string, y: string) => boolean): Uint32Array {
  const n = bEnd - bStart;
  const prev = new Uint32Array(n + 1);
  const curr = new Uint32Array(n + 1);
  for (let i = aEnd - 1; i >= aStart; i--) {
    curr.fill(0);
    for (let j = n - 1; j >= 0; j--) {
      if (eq(a[i], b[bStart + j])) {
        curr[j] = prev[j + 1] + 1;
      } else {
        curr[j] = Math.max(prev[j], curr[j + 1]);
      }
    }
    prev.set(curr);
  }
  return prev;
}

/** Where a word may be cut into sub-word pieces: between user-perceived characters (grapheme
 *  clusters: never inside a surrogate pair, an emoji ZWJ sequence or a letter and its combining
 *  accent). Without Intl.Segmenter, at least never inside a surrogate pair. */
let graphemes: any;
function cutPoints(w: string): (i: number) => boolean {
  if (graphemes === undefined) {
    const Seg = (globalThis as any).Intl?.Segmenter;
    try { graphemes = Seg ? new Seg(undefined, { granularity: 'grapheme' }) : null; } catch { graphemes = null; }
  }
  if (graphemes) {
    const at = new Set<number>([w.length]);
    for (const g of graphemes.segment(w)) at.add(g.index);
    return (i) => at.has(i);
  }
  return (i) => !(i > 0 && i < w.length && /[\uDC00-\uDFFF]/.test(w[i]) && /[\uD800-\uDBFF]/.test(w[i - 1]));
}

/**
 * Refine isolated 1:1 word replacements into sub-word edits when the change inside
 * the word is genuinely small. Turns the common case of a punctuation or small
 * in-word change (e.g. "Site" → "Site,", "indemnities;" → "indemnities",
 * "(30)" → "(60)", "Declaration" → "Declarations") from a whole-word delete+insert
 * into "keep the shared part, mark only what changed". Genuinely different words
 * are left as whole-word replacements — both those that share little ("Purchaser" →
 * "Buyer") and those that merely share a prefix/suffix ("Property" → "Premises",
 * which must read as a word swap, as Word Compare and Litera show it, not as
 * "Pr~~operty~~emises").
 *
 * Sub-word pieces carry glued=true (after the first) so no space is inserted
 * between them. Each piece keeps the parent word's token index for formatting.
 */
/**
 * Whether a word replaced by another is better shown as an edit inside the word, and where:
 * the length of their shared start (p) and end (s), or null for a whole-word swap. Requires
 *  - a NON-EMPTY common prefix (p >= 1) so the first emitted piece is an `equal` that carries
 *    the word's leading inter-word space. If the first piece were a delete/insert, that space
 *    would be absorbed into the revision run and lost when the change is accepted/rejected
 *    (e.g. "[Laughlin" → "Laughlin" must not glue onto the previous word).
 *  - the shared part dominates the shorter word, so dissimilar words stay whole-word swaps.
 *  - the change itself is SMALL: either one side's changed middle is empty (a pure insertion
 *    or deletion of characters — "Declaration" → "Declarations", "owner." → "owner,") or both
 *    middles are at most two characters ("(30)" → "(60)", "Lessee" → "Lessor"). A shared
 *    prefix and suffix around a larger substitution ("Property" → "Premises": "Pr" +
 *    "operty"/"emises") is a different word, not an edit to the same word.
 * Never cuts inside a character ("👩‍⚖️" → "👨‍⚖️" share their first UTF-16 unit).
 */
export function subWordSplit(a: string, b: string): { p: number; s: number } | null {
  let p = 0;
  while (p < a.length && p < b.length && a[p] === b[p]) p++;
  let s = 0;
  while (s < a.length - p && s < b.length - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s++;
  if (/[^\x00-\x7F]/.test(a) || /[^\x00-\x7F]/.test(b)) {
    const ca = cutPoints(a), cb = cutPoints(b);
    while (p > 0 && !(ca(p) && cb(p))) p--;
    while (s > 0 && !(ca(a.length - s) && cb(b.length - s))) s--;
  }
  const shared = p + s;
  const minLen = Math.min(a.length, b.length);
  const delMid = a.slice(p, a.length - s), insMid = b.slice(p, b.length - s);
  const smallChange = delMid.length === 0 || insMid.length === 0 || (delMid.length <= TUNING.SUBWORD_MAX_SWAP && insMid.length <= TUNING.SUBWORD_MAX_SWAP);
  return p >= 1 && minLen >= TUNING.SUBWORD_MIN_LENGTH && shared >= Math.ceil(minLen * TUNING.SUBWORD_SHARED_SHARE) && smallChange ? { p, s } : null;
}

// Punctuation that sits at the edge of a word: `Sprocket,` `("Tenant")` `obligations.`
// Internal punctuation (`3.1(a`, `Lessee's`, `$1,000.00`, `non-refundable`) stays in the word.
const EDGE_PUNCT = /^[.,;:!?()[\]{}"'“”„‟‘’‚‛«»…*–—-]$/;

interface SubTok { text: string; word: number; off: number; sp: boolean; punct: boolean }

/** A changed word cut into its leading punctuation marks (one piece each), its core, and its
 *  trailing punctuation marks: `("Tenant"),` → ( " Tenant " ) , . `sp` is true on the piece
 *  that starts the word (the space before it). A word that is all punctuation stays whole. */
function edgeSplit(w: string, word: number, out: SubTok[]): void {
  if (w.charCodeAt(0) === 1) { out.push({ text: w, word, off: 0, sp: true, punct: false }); return; } // an atomic block (see refineWordReplacements)
  const cut = /[^\x00-\x7F]/.test(w) ? cutPoints(w) : () => true;
  let lead = 0;
  while (lead < w.length && EDGE_PUNCT.test(w[lead]) && cut(lead + 1)) lead++;
  let trail = w.length;
  while (trail > lead && EDGE_PUNCT.test(w[trail - 1]) && cut(trail - 1)) trail--;
  if (trail === lead) { out.push({ text: w, word, off: 0, sp: true, punct: true }); return; }
  for (let i = 0; i < lead; i++) out.push({ text: w[i], word, off: i, sp: i === 0, punct: true });
  out.push({ text: w.slice(lead, trail), word, off: lead, sp: lead === 0, punct: false });
  for (let i = trail; i < w.length; i++) out.push({ text: w[i], word, off: i, sp: false, punct: true });
}

/** Same piece on both sides: same text (quote style aside) and both start a word, or neither. */
const sameSub = (a: SubTok, b: SubTok) => a.sp === b.sp && wordsEqual(a.text, b.text);
// A kept closing mark needs its opening mark kept too: `(B)` must not come out as `{+(B+})`.
const OPENER: Record<string, string> = { ')': '(', ']': '[', '}': '{', '"': '"' };

/**
 * The punctuation kept in one stretch of changed pieces (between two kept words, or a kept word
 * and the edge of the changed run), added to `pairs`. Kept:
 *  - marks stuck to a kept word on both sides: `“Counterparts”[-,-] [-“Termination”-]`;
 *  - the marks both sides end the stretch with: `[-Assessment-] {+System+}.`, `Sprocket
 *    {+Holdings+},`, unless one is a closing bracket or quote whose opening one is changed;
 *  - the marks both sides start it with, when they are stuck to the kept word before them, or
 *    when what follows starts a new word, or is a one-word swap (`("[-Tenant-]{+Lessee+}")`),
 *    or a one-word swap closed by the same marks (`([-b-]{+a+}) [-All-] {+The+}`). Anything
 *    else would leave a lone mark glued to a rewrite, with the struck and inserted text run
 *    together (`([-b) All-]{+a) The+}`).
 * Marks that only line up in the middle of a rewrite (`A, B, C` → `X, Y, Z`) stay part of it.
 */
function keepEdgePunct(o: SubTok[], r: SubTok[], oa: number, ob: number, ra: number, rb: number, pairs: [number, number][]): void {
  const kept: [number, number][] = [];
  // Marks stuck to the kept word before the stretch (its trailing marks) or after it (its
  // leading marks): keep what the two drafts share at either end of them, like an edit
  // inside a word: `Counterparts”`, `outcome[-.-]”`, `Tenant[-)-],`.
  const stuck = (n: number, at: (k: number) => [number, number]) => {
    let p = 0;
    while (p < n && sameSub(o[at(p)[0]], r[at(p)[1]])) p++;
    return p;
  };
  // trailing marks of the kept word before: o[oa..oa+tO), r[ra..ra+tR)
  let tO = 0, tR = 0;
  while (oa + tO < ob && o[oa + tO].punct && !o[oa + tO].sp) tO++;
  while (ra + tR < rb && r[ra + tR].punct && !r[ra + tR].sp) tR++;
  const p1 = stuck(Math.min(tO, tR), k => [oa + k, ra + k]);
  const q1 = stuck(Math.min(tO, tR) - p1, k => [oa + tO - 1 - k, ra + tR - 1 - k]);
  for (let k = 0; k < p1; k++) kept.push([oa + k, ra + k]);
  for (let k = 0; k < q1; k++) kept.push([oa + tO - 1 - k, ra + tR - 1 - k]);
  let i = q1 ? oa + tO : oa + p1, j = q1 ? ra + tR : ra + p1;
  // leading marks of the kept word after: o[ob-lO..ob), r[rb-lR..rb) (the first starts the word)
  let oe = ob, re = rb;
  if (ob < o.length && !o[ob].sp) {
    let lO = 0, lR = 0;
    while (ob - lO - 1 >= i && o[ob - lO - 1].punct && !o[ob - lO].sp) lO++;
    while (rb - lR - 1 >= j && r[rb - lR - 1].punct && !r[rb - lR].sp) lR++;
    const q2 = stuck(Math.min(lO, lR), k => [ob - 1 - k, rb - 1 - k]);
    const p2 = stuck(Math.min(lO, lR) - q2, k => [ob - lO + k, rb - lR + k]);
    for (let k = 0; k < q2; k++) kept.push([ob - 1 - k, rb - 1 - k]);
    for (let k = 0; k < p2; k++) kept.push([ob - lO + k, rb - lR + k]);
    oe = p2 ? ob - lO : ob - q2; re = p2 ? rb - lR : rb - q2;
  }
  // the marks both sides end the stretch with
  let s = 0;
  while (oe - s > i && re - s > j && o[oe - 1 - s].punct && sameSub(o[oe - 1 - s], r[re - 1 - s])) s++;
  const ce = oe - s, cre = re - s;
  // the marks both sides start it with
  for (;;) {
    let p = 0;
    while (i + p < ce && j + p < cre && o[i + p].punct && sameSub(o[i + p], r[j + p])) p++;
    if (!p) break;
    const ni = i + p, nj = j + p;
    const glued = !o[i].sp;   // stuck to a kept mark before it
    const wordStart = (ni >= ce || o[ni].sp) && (nj >= cre || r[nj].sp);
    const single = ni + 1 === ce && nj + 1 === cre && !o[ni].punct && !r[nj].punct;
    let c = 0;
    if (!glued && !wordStart && !single && ni < ce && nj < cre && !o[ni].punct && !r[nj].punct) {
      while (ni + 1 + c < ce && nj + 1 + c < cre && o[ni + 1 + c].punct && !o[ni + 1 + c].sp && sameSub(o[ni + 1 + c], r[nj + 1 + c])) c++;
    }
    if (!(glued || wordStart || single || c)) break;
    for (let k = 0; k < p; k++) kept.push([i + k, j + k]);
    if (c) {
      for (let k = 0; k < c; k++) kept.push([ni + 1 + k, nj + 1 + k]);
      i = ni + 1 + c; j = nj + 1 + c;
      continue;
    }
    i = ni; j = nj;
    if (single) break;
  }
  // the end marks: drop any closing mark whose opening mark is among the changed pieces
  const keptO = new Set(kept.map(k => k[0])), keptR = new Set(kept.map(k => k[1]));
  let drop = 0;
  for (let again = true; again;) {
    again = false;
    const opened = new Set<string>();
    for (let k = oa; k < ce + drop; k++) if (!keptO.has(k)) for (const ch of normalizeQuotes(o[k].text)) opened.add(ch);
    for (let k = ra; k < cre + drop; k++) if (!keptR.has(k)) for (const ch of normalizeQuotes(r[k].text)) opened.add(ch);
    for (let k = drop; k < s; k++) {
      for (const ch of normalizeQuotes(o[ce + k].text) + normalizeQuotes(r[cre + k].text)) {
        if (OPENER[ch] && opened.has(OPENER[ch])) { drop = k + 1; again = true; }
      }
    }
  }
  for (let k = drop; k < s; k++) kept.push([ce + k, cre + k]);
  pairs.push(...kept);
}

/**
 * A run of changed words (deletes and inserts between two unchanged words) re-diffed with
 * punctuation cut off the edges of each word, so a change next to punctuation marks only
 * the words that changed: `Sprocket,` → `Sprocket Holdings,` keeps `Sprocket` and the
 * comma and inserts `Holdings`, where the word diff (which splits on spaces only) could
 * only replace `Sprocket,` with `Sprocket Holdings,`. Returns null when nothing of the run
 * is kept, so the caller keeps the run as it was.
 *
 * Words are matched first, on their cores alone (`Sprocket` in `Sprocket,` and `Sprocket`),
 * so a word and not a stray bracket anchors the alignment; then each stretch between kept
 * words keeps the punctuation at its edges (`keepEdgePunct`).
 *
 * Every piece carries its own leading space on its own side (`glued` when there is none), so
 * the kept pieces plus the deletions always rebuild the original exactly, and the kept
 * pieces plus the insertions the revised text. A piece both sides keep must start its word
 * on both sides or on neither (`[Laughlin` → `Laughlin` stays a whole-word swap: the space
 * before the word would otherwise end up inside a deletion and be lost on accept). Pieces of
 * one word that end up side by side with the same change are joined again.
 */
function refineChangedRun(run: DiffEntry[]): DiffEntry[] | null {
  const dels = run.filter(d => d.type === 'delete'), inss = run.filter(d => d.type === 'insert');
  if (!dels.length || !inss.length) return null;
  const o: SubTok[] = [], r: SubTok[] = [];
  for (const d of dels) edgeSplit(d.value, wordOf(d, 'orig'), o);
  for (const d of inss) edgeSplit(d.value, wordOf(d, 'rev'), r);
  if (o.length === dels.length && r.length === inss.length) return null; // no punctuation to cut off

  // words first: cores, keyed so a word that starts on one side only never matches
  const key = (t: SubTok) => (t.sp ? '\u0001' : '') + t.text;
  const oc = o.map((_, i) => i).filter(i => !o[i].punct), rc = r.map((_, i) => i).filter(i => !r[i].punct);
  const anchors: [number, number][] = [];
  for (const e of diffArrays(oc.map(i => key(o[i])), rc.map(i => key(r[i])), wordsEqual)) {
    if (e.type === 'equal') anchors.push([oc[e.aIdx!], rc[e.bIdx!]]);
  }
  // then the punctuation at the edges of each stretch between them
  const pairs: [number, number][] = [];
  let pi = 0, pj = 0;
  for (const [ai, aj] of [...anchors, [o.length, r.length] as [number, number]]) {
    keepEdgePunct(o, r, pi, ai, pj, aj, pairs);
    if (ai < o.length) { pairs.push([ai, aj]); pi = ai + 1; pj = aj + 1; }
  }
  if (!pairs.length) return null;
  pairs.sort((x, y) => x[0] - y[0]);
  // (kept pieces must run in the same order on both sides; if not, keep the run as it was)
  for (let k = 1; k < pairs.length; k++) if (pairs[k][0] <= pairs[k - 1][0] || pairs[k][1] <= pairs[k - 1][1]) return null;

  const pieces: DiffEntry[] = [];
  const del = (t: SubTok): DiffEntry => ({ type: 'delete', value: t.text, origWordIdx: t.word, origCharIdx: t.off, glued: !t.sp });
  const ins = (t: SubTok): DiffEntry => ({ type: 'insert', value: t.text, revWordIdx: t.word, revCharIdx: t.off, glued: !t.sp });
  let oi = 0, rj = 0;
  for (const [i, j] of [...pairs, [o.length, r.length] as [number, number]]) {
    for (; oi < i; oi++) pieces.push(del(o[oi]));
    for (; rj < j; rj++) pieces.push(ins(r[rj]));
    if (i < o.length) {
      pieces.push({ type: 'equal', value: r[j].text, origWordIdx: o[i].word, revWordIdx: r[j].word,
        origCharIdx: o[i].off, revCharIdx: r[j].off, glued: !r[j].sp });
      oi = i + 1; rj = j + 1;
    }
  }
  // join pieces of the same word that sit side by side with the same change
  const out: DiffEntry[] = [];
  const next = (p: DiffEntry, side: 'orig' | 'rev', q: DiffEntry) => side === 'orig'
    ? q.origWordIdx === p.origWordIdx && q.origCharIdx === p.origCharIdx! + p.value.length
    : q.revWordIdx === p.revWordIdx && q.revCharIdx === p.revCharIdx! + p.value.length;
  for (const q of pieces) {
    const p = out[out.length - 1];
    const join = p && p.type === q.type && q.glued && (
      q.type === 'delete' ? next(p, 'orig', q) :
      q.type === 'insert' ? next(p, 'rev', q) :
      next(p, 'orig', q) && next(p, 'rev', q));
    if (join) p.value += q.value;
    else out.push(q);
  }
  for (const d of out) if (!d.glued) delete d.glued;
  return out;
}

/**
 * The word diff's post-pass, shared by every redline (body, cells, notes, headers and footers):
 * an edit inside a word when a one-word swap is small, and punctuation and words kept beside
 * the words that changed. Entries need origWordIdx/revWordIdx (the word's index on its side).
 * A word starting with U+0001 is an atomic block (a header or footer field) and is never cut.
 */
export function refineWordReplacements(diff: DiffEntry[]): DiffEntry[] {
  const out: DiffEntry[] = [];
  const changed = (e?: DiffEntry) => !!e && (e.type === 'delete' || e.type === 'insert');
  for (let i = 0; i < diff.length; i++) {
    const d = diff[i];
    if (!changed(d)) { out.push(d); continue; }
    // the run of changed words starting here, between two unchanged words
    let k = i;
    while (changed(diff[k + 1])) k++;
    const run = diff.slice(i, k + 1);
    i = k;
    // An ISOLATED single delete immediately followed by a single insert (not part of a
    // longer insert-run) is a 1:1 swap: an edit inside the word when the change is small.
    const n = run[1];
    const cut = d.type === 'delete' && n && n.type === 'insert' && (!run[2] || run[2].type !== 'insert') &&
      d.value.charCodeAt(0) !== 1 && n.value.charCodeAt(0) !== 1 ? subWordSplit(d.value, n.value) : null;
    if (cut) {
      const a = d.value, b = n.value;
      const { p, s } = cut;
      const delMid = a.slice(p, a.length - s);
      const insMid = b.slice(p, b.length - s);
      const prefix = a.slice(0, p);
      const suffix = a.slice(a.length - s); // identical to b's suffix
      let first = true;
      const push = (e: DiffEntry) => { if (!first) e.glued = true; first = false; out.push(e); };
      // Each piece records its character offset within the word so the emitters can
      // keep per-run formatting (bold term, non-bold `")`) on sub-word pieces too.
      if (prefix) push({ type: 'equal', value: prefix, origWordIdx: d.origWordIdx, revWordIdx: n.revWordIdx, origCharIdx: 0, revCharIdx: 0 });
      if (delMid) push({ type: 'delete', value: delMid, origWordIdx: d.origWordIdx, origCharIdx: p });
      if (insMid) push({ type: 'insert', value: insMid, revWordIdx: n.revWordIdx, revCharIdx: p });
      if (suffix) push({ type: 'equal', value: suffix, origWordIdx: d.origWordIdx, revWordIdx: n.revWordIdx, origCharIdx: a.length - s, revCharIdx: b.length - s });
      for (const e of run.slice(2)) out.push(e);
      continue;
    }
    // Otherwise: keep punctuation and words that only differed by the punctuation stuck to them.
    out.push(...(refineChangedRun(run) || run));
  }
  return out;
}

/**
 * Word-level diff between two texts, annotated with original/revised token indices
 * and refined into granular sub-word edits. Shared by the paragraph "modify" path
 * and the moved-and-edited path (so a relocated, lightly-edited block can show its
 * internal insertions/deletions instead of an opaque moved block).
 */
export function computeWordDiff(origText: string, revText: string): DiffEntry[] {
  const origWords = tokenize(origText);
  const revWords = tokenize(revText);
  const wordDiff = withWordIndices(diffArrays(origWords, revWords, wordsEqual));

  const prefiltered: DiffEntry[] = [];
  for (const d of wordDiff) {
    if ((d.type === 'delete' || d.type === 'insert') && !d.value.trim()) continue;
    prefiltered.push(d);
  }
  return refineWordReplacements(prefiltered);
}


// ---------------------------------------------------------------------------
// Similarity scoring is O(pairs) across paragraphs, but the derived word forms
// of each paragraph are pure functions of its text — computing them per PAIR
// (and lowercasing per LCS cell) dominated large-document diffs (~70% of total
// runtime on an 850-paragraph pair). Cache them per text. The derivations are
// byte-for-byte the ones the scorers used inline, so scores are unchanged.
// ---------------------------------------------------------------------------
interface TextForms { simLower: string[]; cheapSet: Set<string>; masks?: Map<string, Uint32Array> }
const _formsCache = new Map<string, TextForms>();
const _FORMS_CACHE_MAX = 5000;
function formsOf(text: string): TextForms {
  let f = _formsCache.get(text);
  if (f) return f;
  const norm = normalizeText(text);
  const simLower = norm.split(/\s+/).map(w => w.toLowerCase());
  f = { simLower, cheapSet: new Set(norm.toLowerCase().split(/\s+/)) };
  if (_formsCache.size >= _FORMS_CACHE_MAX) _formsCache.clear();
  _formsCache.set(text, f);
  return f;
}

export function similarity(a: string | null | undefined, b: string | null | undefined): number {
  const sa = a || '';
  const sb = b || '';
  if (!sa.trim() && !sb.trim()) return 1;
  if (!sa.trim() || !sb.trim()) return 0;

  // lowercased word arrays, cached per text (identical to the inline derivation)
  const wordsA = formsOf(sa).simLower;
  const wordsB = formsOf(sb).simLower;

  if (wordsA.length === 0 && wordsB.length === 0) return 1;
  if (wordsA.length === 0 || wordsB.length === 0) return 0;

  const m = wordsA.length, n = wordsB.length;
  return (2 * lcsLength(formsOf(sa), wordsB)) / (m + n);
}

/** Popcount of a 32-bit value. */
function bitCount(x: number): number {
  x = x - ((x >>> 1) & 0x55555555);
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333);
  return (((x + (x >>> 4)) & 0x0F0F0F0F) * 0x01010101) >>> 24;
}
/** For each word of a paragraph, the bit mask of its positions (cached with the text's forms). */
function positionMasks(f: TextForms): Map<string, Uint32Array> {
  if (f.masks) return f.masks;
  const words = f.simLower, W = (words.length + 31) >>> 5;
  const masks = new Map<string, Uint32Array>();
  for (let i = 0; i < words.length; i++) {
    let v = masks.get(words[i]);
    if (!v) { v = new Uint32Array(W); masks.set(words[i], v); }
    v[i >>> 5] |= 1 << (i & 31);
  }
  return (f.masks = masks);
}
/**
 * Length of the longest common subsequence of a paragraph's words (as `formsOf`) and `wordsB`,
 * bit-parallel (Allison–Dix / Hyyrö): O(|A|·|B|/32) instead of the O(|A|·|B|) table, which was
 * most of the time spent matching the paragraphs of a long document (a 320-page draft: 24 s
 * of 30). Exact: the same length the table gives. V starts all ones; for each word y of B,
 * U = V & M[y] and V = (V + U) | (V − U), carried and borrowed across 32-bit words; the LCS
 * is the number of zero bits left in the |A| low bits.
 */
function lcsLength(fa: TextForms, wordsB: string[]): number {
  const m = fa.simLower.length, W = (m + 31) >>> 5;
  const masks = positionMasks(fa);
  const V = new Uint32Array(W).fill(0xFFFFFFFF);
  for (let j = 0; j < wordsB.length; j++) {
    const M = masks.get(wordsB[j]);
    if (!M) continue;   // U = 0 leaves V as it is
    let carry = 0, borrow = 0;
    for (let k = 0; k < W; k++) {
      const v = V[k], u = (v & M[k]) >>> 0;
      const sum = v + u + carry;
      carry = sum > 0xFFFFFFFF ? 1 : 0;
      const diff = v - u - borrow;
      borrow = diff < 0 ? 1 : 0;
      V[k] = ((sum >>> 0) | (diff >>> 0)) >>> 0;
    }
  }
  let zeros = 0;
  for (let k = 0; k < W; k++) {
    let x = (~V[k]) >>> 0;
    const r = k === W - 1 ? m & 31 : 0;
    if (r) x &= ((1 << r) >>> 0) - 1;
    zeros += bitCount(x);
  }
  return zeros;
}

export function cheapSimilarity(textA: string, textB: string): number {
  if (!textA.trim() && !textB.trim()) return 1;
  if (!textA.trim() || !textB.trim()) return 0;
  const setA = formsOf(textA).cheapSet;
  const setB = formsOf(textB).cheapSet;
  let intersection = 0;
  for (const w of setA) { if (setB.has(w)) intersection++; }
  return (2 * intersection) / (setA.size + setB.size);
}

function yieldToUI(): Promise<void> {
  return new Promise(r => setTimeout(r, 0));
}

export async function matchParagraphs(origParas: RichParagraph[], revParas: RichParagraph[]): Promise<any[]> {
  const m = origParas.length;
  const n = revParas.length;

  const simCache = new Map<number, number>();
  function getSim(i: number, j: number): number {
    const key = i * (n + 1) + j;
    const cached = simCache.get(key);
    if (cached !== undefined) return cached;
    const cheap = cheapSimilarity(origParas[i].text, revParas[j].text);
    if (cheap < TUNING.MATCH_PREFILTER) { simCache.set(key, cheap); return cheap; }
    const s = similarity(origParas[i].text, revParas[j].text);
    simCache.set(key, s);
    return s;
  }

  const MATCH_THRESHOLD = TUNING.MATCH_THRESHOLD;
  const GAP_PENALTY = 0;

  const BAND_WIDTH = Math.max(TUNING.MATCH_BAND_MIN, Math.ceil(Math.max(m, n) * TUNING.MATCH_BAND_SHARE));
  const bandDiam = 2 * BAND_WIDTH + 1;

  const dpPrev = new Float64Array(n + 1);
  const dpCurr = new Float64Array(n + 1);

  // Compact banded backtrack: store only band entries per row in a flat Uint8Array.
  // For row i, band center is round(i * scale), and we store columns [jMin..jMax].
  // btBand[i] stores { offset: jMin, data: Uint8Array of length jMax-jMin+1 }.
  // Default backtrack: row 0 = 2 (insert), column 0 = 1 (delete), out-of-band = 1 (delete).
  const btBandData: Uint8Array[] = new Array(m + 1);
  const btBandOffset: Int32Array = new Int32Array(m + 1);

  // Row 0: all inserts
  btBandData[0] = new Uint8Array(n + 1);
  btBandData[0].fill(2);
  btBandData[0][0] = 0;
  btBandOffset[0] = 0;

  const scale = n / Math.max(m, 1);

  for (let i = 1; i <= m; i++) { dpPrev[0] = 0; }
  for (let j = 1; j <= n; j++) { dpPrev[j] = 0; }

  for (let i = 1; i <= m; i++) {
    dpCurr.fill(-Infinity);
    dpCurr[0] = 0;

    const center = Math.round(i * scale);
    const jMin = Math.max(1, center - BAND_WIDTH);
    const jMax = Math.min(n, center + BAND_WIDTH);
    const bandLen = jMax - jMin + 1;

    const rowBt = new Uint8Array(bandLen);
    rowBt.fill(1); // default: delete (skip orig paragraph)

    for (let j = jMin; j <= jMax; j++) {
      const sim = getSim(i-1, j-1);
      const prevDiag = (dpPrev[j-1] > -Infinity) ? dpPrev[j-1] : -Infinity;
      const prevUp = (dpPrev[j] > -Infinity) ? dpPrev[j] : -Infinity;
      const prevLeft = (dpCurr[j-1] > -Infinity) ? dpCurr[j-1] : -Infinity;
      const matchScore = prevDiag + sim;
      const delScore = prevUp + GAP_PENALTY;
      const insScore = prevLeft + GAP_PENALTY;

      if (matchScore >= delScore && matchScore >= insScore && sim >= MATCH_THRESHOLD) {
        dpCurr[j] = matchScore;
        rowBt[j - jMin] = 0;
      } else if (delScore >= insScore) {
        dpCurr[j] = delScore;
        rowBt[j - jMin] = 1;
      } else {
        dpCurr[j] = insScore;
        rowBt[j - jMin] = 2;
      }

      if (sim < MATCH_THRESHOLD && !origParas[i-1].text.trim() && !revParas[j-1].text.trim()) {
        const emptyScore = (prevDiag > -Infinity) ? prevDiag + 1 : -Infinity;
        if (emptyScore > dpCurr[j]) {
          dpCurr[j] = emptyScore;
          rowBt[j - jMin] = 0;
        }
      }
    }

    btBandData[i] = rowBt;
    btBandOffset[i] = jMin;

    dpPrev.set(dpCurr);
    if (i % 50 === 0) await yieldToUI();
  }

  // Backtrack using banded storage
  function getBt(i: number, j: number): number {
    if (i === 0 && j === 0) return 0;
    if (i === 0) return 2; // insert
    if (j === 0) return 1; // delete
    const off = btBandOffset[i];
    const idx = j - off;
    if (idx < 0 || idx >= btBandData[i].length) return 1; // out of band: delete
    return btBandData[i][idx];
  }

  const alignment: { oi: number; ri: number }[] = [];
  let ii = m, jj = n;
  while (ii > 0 || jj > 0) {
    const dir = getBt(ii, jj);
    if (ii > 0 && jj > 0 && dir === 0) {
      alignment.push({ oi: ii-1, ri: jj-1 });
      ii--; jj--;
    } else if (ii > 0 && dir === 1) {
      alignment.push({ oi: ii-1, ri: -1 });
      ii--;
    } else {
      alignment.push({ oi: -1, ri: jj-1 });
      jj--;
    }
  }
  alignment.reverse();

  // Between two matched paragraphs, the deleted ones come before the inserted ones: old text
  // first, as inside a paragraph and as Word and Litera show it. (The backtrack, walking from
  // the end, put each inserted paragraph ahead of the deleted one beside it.) Each side keeps
  // its own order, so accepting or rejecting everything gives the same documents.
  for (let s = 0; s < alignment.length; ) {
    if (alignment[s].oi >= 0 && alignment[s].ri >= 0) { s++; continue; }
    let e = s;
    while (e < alignment.length && !(alignment[e].oi >= 0 && alignment[e].ri >= 0)) e++;
    const gap = alignment.slice(s, e);
    const dels = gap.filter(a => a.ri < 0), inss = gap.filter(a => a.oi < 0);
    if (dels.length && inss.length) alignment.splice(s, gap.length, ...dels, ...inss);
    s = e;
  }

  // Free backtrack memory
  for (let i = 0; i <= m; i++) btBandData[i] = null!;

  const result: any[] = [];
  for (const a of alignment) {
    if (a.oi >= 0 && a.ri >= 0) {
      const orig = origParas[a.oi];
      const rev = revParas[a.ri];
      if (normalizeText(orig.text) === normalizeText(rev.text)) {
        result.push({ type: 'equal', original: orig, revised: rev });
      } else {
        result.push({ type: 'modify', original: orig, revised: rev });
      }
    } else if (a.oi >= 0) {
      result.push({ type: 'delete', original: origParas[a.oi], revised: null });
    } else {
      result.push({ type: 'insert', original: null, revised: revParas[a.ri] });
    }
  }

  return result;
}

// Full document diff
export async function diffDocuments(origRichParas: RichParagraph[], revRichParas: RichParagraph[]): Promise<DiffResult> {
  const paraMatches = await matchParagraphs(origRichParas, revRichParas);

  const result: DiffParagraph[] = [];

  let pmIdx = 0;
  for (const pm of paraMatches) {
    pmIdx++;
    if (pmIdx % 100 === 0) await yieldToUI();
    if (pm.type === 'equal') {
      result.push({
        type: 'equal',
        text: pm.original.text,
        origRich: pm.original,
        revRich: pm.revised
      });
    } else if (pm.type === 'delete') {
      result.push({
        type: 'para-delete',
        text: pm.original.text,
        origRich: pm.original
      });
    } else if (pm.type === 'insert') {
      result.push({
        type: 'para-insert',
        text: pm.revised.text,
        revRich: pm.revised
      });
    } else if (pm.type === 'modify') {
      const sim = similarity(pm.original.text, pm.revised.text);

      // Unbundle very dissimilar paragraphs into delete+insert instead of noisy
      // word-level diffs. Threshold lowered from 0.15 to 0.08 to match Word's
      // behavior of preserving word-level diffs for more paragraph pairs.
      if (sim < TUNING.UNBUNDLE_BELOW && tokenize(pm.original.text).length >= TUNING.UNBUNDLE_MIN_WORDS && tokenize(pm.revised.text).length >= TUNING.UNBUNDLE_MIN_WORDS) {
        result.push({
          type: 'para-delete',
          text: pm.original.text,
          origRich: pm.original,
          _unbundled: true
        });
        result.push({
          type: 'para-insert',
          text: pm.revised.text,
          revRich: pm.revised,
          _unbundled: true
        });
        continue;
      }

      const filteredDiff = computeWordDiff(pm.original.text, pm.revised.text);
      await yieldToUI();

      const hasMeaningfulChanges = filteredDiff.some(d => d.type !== 'equal');

      if (!hasMeaningfulChanges) {
        result.push({
          type: 'equal',
          text: pm.revised.text,
          origRich: pm.original,
          revRich: pm.revised
        });
      } else {
        result.push({
          type: 'modify',
          diff: filteredDiff,
          origRich: pm.original,
          revRich: pm.revised
        });
      }
    }
  }

  // Paragraph splits/merges: same words, different paragraph breaks. Must run
  // BEFORE move detection, which otherwise claims the split-off (or merged-in)
  // text as a moved clause and marks it.
  restructureSplitsAndMerges(result);

  // Move detection pass — runs for all tiers
  detectMoves(result);

  // the word counts, once, from the finished result (counts.ts)
  return { paragraphs: result, stats: countWords(result) };
}

// ---------------------------------------------------------------------------
// Paragraph split / merge recognition.
//
// A paragraph split in two (or several merged into one) contains exactly the same
// words as before; only the paragraph marks differ. The paragraph matcher can't see
// that — it pairs the original with one piece as a `modify` and leaves the other
// pieces as whole-paragraph inserts (or deletes), so the redline shows half the text
// struck and re-inserted (or, after move detection, as a "moved clause"). Word's own
// Compare shows such an edit as an inserted/deleted PARAGRAPH MARK with every word
// untouched, and that is what a lawyer expects to read.
//
// Detection: a short window of consecutive diff entries made of exactly one `modify`
// plus adjacent whole-paragraph inserts (a split) or deletes (a merge) whose original
// words, concatenated in order, equal the revised words concatenated in order. Such a
// window is replaced by one `equal` entry per revised piece (split) or per original
// piece (merge), with `paraMark` on every piece but the last: 'inserted' marks for a
// split, 'deleted' marks for a merge. Any word-level edit inside the restructured text
// defeats the equality test and the window is left alone (today's behaviour).
// ---------------------------------------------------------------------------
// A split or merge may sit next to UNRELATED whole-paragraph deletes or inserts (a
// paragraph deleted just above the split, a block moved away from right before it), and
// the matcher may have paired a half with a neighbour instead of leaving it a plain
// insert (a deleted paragraph that resembles the split's first half becomes a second
// `modify`), or paired nothing at all (a short heading merged into a long paragraph
// scores below the match threshold). So the pieces are found by WORDS, not by entry
// type: each entry contributes its original side and/or its revised side; the window's
// original words, read in order, must equal its revised words read in order, piece by
// piece. Entries whose text is not part of the restructure stay exactly as they were; a
// `modify` that lent only one side to it keeps the other side as a whole-paragraph
// delete or insert.
const MAX_RESTRUCTURE_PIECES = TUNING.RESTRUCTURE_MAX_PIECES;
const MAX_RESTRUCTURE_WINDOW = TUNING.RESTRUCTURE_WINDOW;
const MAX_RESTRUCTURE_MODIFIES = TUNING.RESTRUCTURE_MAX_MODIFIES;
// A split or merge that also carries a small edit (a word swapped in one half) is still a
// split or merge: up to this many changed words, and never more than a quarter of the text.
const NEAR_MAX_CHANGES = TUNING.NEAR_MAX_CHANGES;

function wordsOfRich(r: RichParagraph | undefined): string[] {
  return r ? tokenize(r.text || '') : [];
}
/** true when `piece` equals `whole[at .. at+piece.length)` word for word. */
function wordsMatchAt(whole: string[], at: number, piece: string[]): boolean {
  if (at + piece.length > whole.length) return false;
  for (let i = 0; i < piece.length; i++) if (!wordsEqual(whole[at + i], piece[i])) return false;
  return true;
}
interface SideItem { entry: DiffParagraph; rich: RichParagraph }
interface Restructure { out: DiffParagraph[]; pieces: number }

/**
 * Try to read `win` as ONE paragraph (`whole`, an original-side item for a split, a
 * revised-side item for a merge) whose words are exactly the concatenation of ≥ 2 pieces
 * taken, in order, from the other side's items. Items on the piece side that do not
 * continue the text are strays and keep their place in that side's order; strays on the
 * whole's side go before or after the pieces according to their order relative to
 * `whole`. A `modify` with neither side used stays a `modify`; one with only its
 * piece-side used leaves its other side as a whole-paragraph delete/insert.
 */
function tryRestructure(win: DiffParagraph[], whole: SideItem, kind: 'split' | 'merge', near = false): Restructure | null {
  const W = tokenize(whole.rich.text || '');
  if (W.length === 0) return null;
  const pieceSide: SideItem[] = [];
  for (const e of win) { const r = kind === 'split' ? e.revRich : e.origRich; if (r) pieceSide.push({ entry: e, rich: r }); }
  const wholeSide: SideItem[] = [];
  for (const e of win) { const r = kind === 'split' ? e.origRich : e.revRich; if (r) wholeSide.push({ entry: e, rich: r }); }

  const pieceOf = new Map<DiffParagraph, number>();     // entry -> piece ordinal
  let nPieces = 0;
  let pieceDiffs: DiffEntry[][] | null = null;          // near mode: each piece's share of the word diff
  if (!near) {
    // exact: greedy walk along the piece side, strays allowed
    let at = 0;
    for (const it of pieceSide) {
      const w = tokenize(it.rich.text || '');
      if (wordsMatchAt(W, at, w)) { pieceOf.set(it.entry, nPieces++); at += w.length; }
    }
    if (at !== W.length || nPieces < 2 || nPieces > MAX_RESTRUCTURE_PIECES) return null;
  } else {
    // near: every piece-side item is a piece; their words may differ from the whole by a
    // few edited words, which stay delete/insert inside the piece that holds them
    if (pieceSide.length < 2 || pieceSide.length > MAX_RESTRUCTURE_PIECES || W.length < TUNING.NEAR_MIN_WORDS) return null;
    const ranges: { start: number; end: number }[] = [];
    const P: string[] = [];
    for (const it of pieceSide) {
      const w = tokenize(it.rich.text || '');
      ranges.push({ start: P.length, end: P.length + w.length });
      P.push(...w);
      pieceOf.set(it.entry, nPieces++);
    }
    if (Math.abs(W.length - P.length) > NEAR_MAX_CHANGES) return null;
    if (cheapSimilarity(W.join(' '), P.join(' ')) < TUNING.NEAR_SIMILARITY) return null;
    const diff = kind === 'split' ? computeWordDiff(W.join(' '), P.join(' ')) : computeWordDiff(P.join(' '), W.join(' '));
    const changed = diff.filter(d => d.type !== 'equal' && d.value.trim()).length;
    if (changed === 0 || changed > Math.min(NEAR_MAX_CHANGES, Math.floor(W.length / TUNING.NEAR_MAX_SHARE))) return null;
    // deal the entries to pieces by their piece-side position; a word the piece side
    // lacks travels with the entry before it
    pieceDiffs = ranges.map(() => []);
    let cur = 0;
    for (const d of diff) {
      const idx = kind === 'split' ? d.revWordIdx : d.origWordIdx;
      if (typeof idx === 'number') while (cur < ranges.length - 1 && idx >= ranges[cur].end) cur++;
      pieceDiffs[cur].push(d);
    }
    // every piece must keep some of the whole's words: a piece made only of new (or
    // only of dropped) words is an inserted or deleted paragraph next to an edited one,
    // not a half of it — an empty paragraph or a short heading must never turn an
    // ordinary edit into a "split"
    if (pieceDiffs.some(es => !es.some(e => e.type === 'equal' && e.value.trim()))) return null;
    pieceDiffs.forEach((es, k) => {
      for (const e of es) {
        if (kind === 'split') { if (typeof e.revWordIdx === 'number') e.revWordIdx -= ranges[k].start; }
        else if (typeof e.origWordIdx === 'number') e.origWordIdx -= ranges[k].start;
      }
    });
  }
  // the whole's own entry, if a modify, must have lent its other side as a piece
  if (whole.entry.type === 'modify' && !pieceOf.has(whole.entry)) return null;

  // every modify in the window: both sides used, one side used, or neither (kept as is)
  const keptModify = new Set<DiffParagraph>();
  const decomposed = new Set<DiffParagraph>();          // modify whose unused side becomes a whole-paragraph delete/insert
  for (const e of win) {
    if (e.type !== 'modify') continue;
    const usedPiece = pieceOf.has(e), usedWhole = e === whole.entry;
    if (!usedPiece && !usedWhole) keptModify.add(e);
    else if (usedPiece !== usedWhole) decomposed.add(e);
  }

  const mkPiece = (k: number, r: RichParagraph): DiffParagraph => {
    const last = k === nPieces - 1;
    const es = pieceDiffs ? pieceDiffs[k] : null;
    if (es && es.some(e => e.type !== 'equal')) {
      // the piece that holds the edit: word marks for the edit, paragraph mark as usual.
      // Word indices on the whole's side stay absolute, so formatting lookups still work.
      return kind === 'split'
        ? { type: 'modify', diff: es, origRich: whole.rich, revRich: r, ...(last ? {} : { paraMark: 'inserted' as const }) }
        : { type: 'modify', diff: es, origRich: r, revRich: whole.rich, ...(last ? {} : { paraMark: 'deleted' as const }) };
    }
    return kind === 'split'
      ? { type: 'equal', text: r.text, revRich: r, origRich: k === 0 ? whole.rich : undefined, ...(last ? {} : { paraMark: 'inserted' as const }) }
      : { type: 'equal', text: r.text, origRich: r, ...(last ? {} : { paraMark: 'deleted' as const }) };
  };
  const strayOf = (e: DiffParagraph, side: 'orig' | 'rev'): DiffParagraph =>
    e.type === 'modify'
      ? (side === 'orig' ? { type: 'para-delete', text: e.origRich!.text, origRich: e.origRich } : { type: 'para-insert', text: e.revRich!.text, revRich: e.revRich })
      : e;

  // piece-side stream in its own order: pieces, stray inserts/deletes, kept modifies
  const stream: DiffParagraph[] = [];
  for (const it of pieceSide) {
    const k = pieceOf.get(it.entry);
    if (k != null) stream.push(mkPiece(k, it.rich));
    else if (keptModify.has(it.entry)) stream.push(it.entry);
    else stream.push(strayOf(it.entry, kind === 'split' ? 'rev' : 'orig'));
  }
  // whole-side strays: before/after the pieces by their order relative to `whole`
  const before: DiffParagraph[] = [], after: DiffParagraph[] = [];
  let past = false;
  for (const it of wholeSide) {
    if (it.entry === whole.entry) { past = true; continue; }
    if (keptModify.has(it.entry) || pieceOf.has(it.entry) && it.entry.type === 'modify' && !decomposed.has(it.entry)) continue;
    if (it.entry.type === 'modify' && !decomposed.has(it.entry)) continue;
    (past ? after : before).push(strayOf(it.entry, kind === 'split' ? 'orig' : 'rev'));
  }
  return { out: [...before, ...stream, ...after], pieces: nPieces };
}

export function restructureSplitsAndMerges(result: DiffParagraph[]): void {
  const isRow = (r?: RichParagraph) => !!(r && (r.isTableRow || r.cellData));
  const isPlain = (e: DiffParagraph) => e.type === 'modify' || e.type === 'para-insert' || e.type === 'para-delete';
  const touchesRow = (e: DiffParagraph) => isRow(e.origRich) || isRow(e.revRich);
  let i = 0;
  while (i < result.length) {
    if (!isPlain(result[i]) || touchesRow(result[i])) { i++; continue; }
    let found: Restructure | null = null;
    let winLen = 0;
    // Exact matches over every window size first; only when none exists anywhere is a
    // window re-read as a split or merge carrying a small edit — otherwise a three-way
    // split with a short last piece would be taken for a two-way split that dropped words.
    for (const near of [false, true]) {
      for (let j = i + 1; j < result.length && j - i < MAX_RESTRUCTURE_WINDOW; j++) {
        const e = result[j];
        if (!isPlain(e) || touchesRow(e)) break;
        const win = result.slice(i, j + 1);
        if (win.filter(w => w.type === 'modify').length > MAX_RESTRUCTURE_MODIFIES) break;
        const origs: SideItem[] = [], revs: SideItem[] = [];
        for (const w of win) { if (w.origRich) origs.push({ entry: w, rich: w.origRich }); if (w.revRich) revs.push({ entry: w, rich: w.revRich }); }
        if (!near) {
          if (revs.length >= 2) for (const o of origs) { found = tryRestructure(win, o, 'split'); if (found) break; }
          if (!found && origs.length >= 2) for (const r of revs) { found = tryRestructure(win, r, 'merge'); if (found) break; }
        } else {
          if (revs.length >= 2 && origs.length === 1) found = tryRestructure(win, origs[0], 'split', true);
          if (!found && origs.length >= 2 && revs.length === 1) found = tryRestructure(win, revs[0], 'merge', true);
        }
        if (found) { winLen = win.length; break; }
      }
      if (found) break;
    }
    if (!found) { i++; continue; }
    result.splice(i, winLen, ...found.out);
    i += found.out.length;
  }
}
